import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { map, take } from 'rxjs/operators';
import { merge, Observable } from 'rxjs';
import { DeleteConfirmComponent } from '@core/dialogs/delete-confirm/delete-confirm.component';
import { ActionConfirmationComponent } from '@core/dialogs/action-confirmation/action-confirmation.component';
import { ImageCropperDialogComponent } from '@core/dialogs/image-cropper/image-cropper-dialog.component';
import { AddRoomComponent } from '@core/dialogs/add-room/add-room.component';
import {
	ChangeFloorNameComponent,
	ChangeFloorNameData,
} from '@core/dialogs/change-floor-name/change-floor-name.component';
import { ConfirmSensorInstallationModalComponent } from '@core/dialogs/confirm-sensor-installation-modal/confirm-sensor-installation-modal.component';
import { AlertPopoverComponent } from '@core/dialogs/alert-popover/alert-popover.component';
import { CMMSCopyDialogComponent } from '@core/dialogs/cmms-copy-dialog/cmms-copy-dialog.component';
import { ActionCenterComponent } from '../dialogs/action-center/action-center.component';
import {
	IAssetBuilding,
	ISensorInstallation,
} from '@app/pages/settings/portfolio/interfaces/area-assets.interfaces';
import { ActionTypes, IActions } from '@app/shared/action-center/interfaces';
import { ViewFolderCopyComponent } from '@core/dialogs/view-folder-copy/view-folder-copy.component';
import { AddContactComponent } from '../dialogs/add-contact/add-contact.component';
import { SelectionModel } from '@angular/cdk/collections';
import { ICmmsEntitiesList } from '@app/pages/cmms/components/table-elements/interfaces/interfaces';
import { TextDialogComponent } from '../dialogs/text-dialog/text-dialog.component';
import { ComponentOverlayDialogComponent } from '../dialogs/component-overlay-dialog/component-overlay-dialog.component';
import { ComponentType } from '@angular/cdk/portal';
import { EntityDialogComponents, IChecklistOption } from '@app/domain/types';
import { AddChecklistItemDialogComponent } from '../dialogs/add-checklist-item-dialog/add-checklist-item-dialog.component';
import { EditLocationDialogComponent } from '@app/core/dialogs/edit-location-dialog/edit-location-dialog.component';
import {
	EditLocationData,
	EditLocationModalComponent,
} from '../dialogs/edit-location/edit-location.component';
import { EditTitleDialogComponent } from '../dialogs/edit-title-dialog/edit-title-dialog.component';
import { CopyRoutineDialogComponent } from '../dialogs/copy-routine-dialog/copy-routine-dialog.component';

export interface IAlertPopoverData {
	title: string;
	actionBtnText: string;
	description: string;
}

export interface IDeleteDialogData {
	itemType?: string;
	itemName?: string;
	title?: string;
	description?: string;
	confirmButton?: string;
}

export interface IActionConfirmationData {
	header?: string;
	actionDescription: string;
	confirmButton?: string;
}

export interface ICropDialogData {
	title: string;
	isProfilePicture?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class AuDialogService {
	constructor(private dialog: MatDialog) {}

	openDeleteDialog(dialogData: IDeleteDialogData): MatDialogRef<DeleteConfirmComponent> {
		return this.dialog.open(DeleteConfirmComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'action'],
		});
	}

	openAlertDialog(dialogData: IAlertPopoverData): MatDialogRef<AlertPopoverComponent> {
		return this.dialog.open(AlertPopoverComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'action'],
		});
	}

	openConfirmationDialog(
		dialogData: IActionConfirmationData
	): MatDialogRef<ActionConfirmationComponent> {
		return this.dialog.open(ActionConfirmationComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'action'],
		});
	}

	openEditLocationDialog(
		dialogData: EditLocationData
	): MatDialogRef<EditLocationModalComponent> {
		return this.dialog.open(EditLocationModalComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: 'au-modal',
		});
	}

	openSensorConfirmationDialog(dialogData: {
		sensor: ISensorInstallation;
	}): MatDialogRef<ConfirmSensorInstallationModalComponent> {
		return this.dialog.open(ConfirmSensorInstallationModalComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: 'au-modal',
		});
	}

	openChangeFloorNameDialog(
		dialogData: ChangeFloorNameData
	): MatDialogRef<ChangeFloorNameComponent> {
		return this.dialog.open(ChangeFloorNameComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: 'au-modal',
		});
	}

	openAddRoomDialog(dialogData?: any): MatDialogRef<AddRoomComponent> {
		return this.dialog.open(AddRoomComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: 'au-modal',
			restoreFocus: false,
		});
	}

	openImageCropperDialog(
		dialogData: ICropDialogData
	): MatDialogRef<ImageCropperDialogComponent, Blob> {
		return this.dialog.open(ImageCropperDialogComponent, {
			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'no-max-width'],
		});
	}

	openCMMSCopyDialog(
		selection: SelectionModel<any>
	): MatDialogRef<CMMSCopyDialogComponent> {
		return this.dialog.open(CMMSCopyDialogComponent, {
			data: selection.selected,
			backdropClass: 'au-modal-backdrop-transparent-bg',
			panelClass: ['au-modal', 'au-cmms-copy-dialog-backdrop', 'no-max-width'],
		});
	}

	openFolderCopyDialog(): MatDialogRef<ViewFolderCopyComponent> {
		return this.dialog.open(ViewFolderCopyComponent, {
			backdropClass: 'au-modal-backdrop-transparent-bg',
			panelClass: ['au-modal', 'au-cmms-copy-dialog-backdrop', 'no-max-width'],
		});
	}

	openActionCenter(
		dialogData: ActionTypes[],
		selectedEntities: ICmmsEntitiesList,
		actionConfigs: IActions,
		setActiveConfig: ActionTypes
	): MatDialogRef<ActionCenterComponent> {
		const configs = [...new Set(dialogData.map(configName => actionConfigs[configName]))];
		return this.dialog.open(ActionCenterComponent, {
			minWidth: '800px',
			maxWidth: '1400px',
			minHeight: '100px',
			maxHeight: '700px',

			data: { configs, selectedEntities, setActiveConfig },
			backdropClass: 'au-modal-backdrop-transparent-bg',
			panelClass: [
				'au-modal',
				'mat-mdc-dialog-actions',
				'au-cmms-action-center-backdrop',
			],
		});
	}

	openContactDialog(initialData = {}, editContact = false): MatDialogRef<any> {
		return this.dialog.open(AddContactComponent, {
			width: '620px',
			minHeight: '100px',

			data: { initialData, editContact },
			backdropClass: 'au-modal-backdrop',
			panelClass: [
				'au-modal',
				'au-cmms-copy-dialog-backdrop',
				'au-cmms-action-center-backdrop',
			],
		});
	}

	openTextDialog(dialogData: { class: string; content: string }[]): MatDialogRef<any> {
		return this.dialog.open(TextDialogComponent, {
			width: '500px',
			maxHeight: '250px',

			data: dialogData,
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal'],
		});
	}

	openComponentOverlayDialog(
		component: ComponentType<EntityDialogComponents>,
		title: string,
		outputEventName: string
	): MatDialogRef<ComponentOverlayDialogComponent> {
		return this.dialog.open(ComponentOverlayDialogComponent, {
			minWidth: '312px',
			minHeight: '600px',
			maxHeight: '700px',
			data: { component, title, outputEventName },
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'action'],
		});
	}

	openAddChecklistItemDialog(
		checklistItems: IChecklistOption[]
	): MatDialogRef<AddChecklistItemDialogComponent> {
		return this.dialog.open(AddChecklistItemDialogComponent, {
			data: checklistItems,
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'action'],
		});
	}

	openChangeLocationDialog(dialogData: {
		building: IAssetBuilding;
	}): MatDialogRef<EditLocationDialogComponent> {
		return this.dialog.open(EditLocationDialogComponent, {
			data: dialogData,
		});
	}

	openEditTitleDialog(currentTitle: string): MatDialogRef<EditTitleDialogComponent> {
		return this.dialog.open(EditTitleDialogComponent, {
			width: '800px',
			data: currentTitle,
			panelClass: ['au-modal'],
		});
	}

	openRoutineCopyDialog() {
		return this.dialog.open(CopyRoutineDialogComponent, {
			height: '500px',
			width: '600px',
			panelClass: ['au-modal'],
		});
	}

	closedByEnter(dialog: MatDialogRef<any>): Observable<boolean> {
		return merge(
			dialog.afterClosed(),
			dialog.keydownEvents().pipe(map(e => e.keyCode === 13)) // 'Enter code = 13'
		).pipe(
			take(1),
			map((...responses) => responses.filter(res => !!res)),
			map(responses => !!responses.length)
		);
	}
}
